<template>
  <LayoutMain>
    <div class="space-y-8">
      <section class="space-y-4">
        <div>
          <p class="font-label text-label-md uppercase text-secondary">
            {{ nextBestAction.eyebrow }}
          </p>
          <h2 class="mt-1 font-headline text-headline-md text-primary">
            {{ t('home.whatToDoNow') }}
          </h2>
        </div>

        <button
          type="button"
          class="flex w-full items-center gap-5 rounded-xl bg-primary p-6 text-left text-on-primary shadow-md transition hover:bg-primary-container active:scale-[0.99]"
          @click="nextBestAction.action"
        >
          <span class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/15">
            <component :is="nextBestAction.icon" class="h-6 w-6" aria-hidden="true" />
          </span>
          <span class="min-w-0 flex-1">
            <span class="block font-headline text-headline-md text-on-primary">
              {{ nextBestAction.title }}
            </span>
            <span class="mt-1 block font-body text-body-sm text-primary-fixed">
              {{ nextBestAction.description }}
            </span>
            <span class="mt-4 inline-flex rounded-full bg-white px-4 py-2 font-label text-label-md text-primary">
              {{ nextBestAction.label }}
            </span>
          </span>
        </button>

        <ZinInSpelStatus />
      </section>

      <section class="rounded-xl border border-outline-variant bg-surface-container-lowest p-5 shadow-sm">
        <div class="mb-5 flex items-baseline justify-between gap-4">
          <div>
            <h2 class="font-headline text-headline-md text-primary">{{ t('home.peopleNearby') }}</h2>
            <p class="mt-1 font-body text-body-sm text-on-surface-variant">
              {{ t('home.peopleNearbyDescription') }}
            </p>
          </div>
        </div>
        <div class="grid gap-5 md:grid-cols-3">
          <ActiveUsersPreview
            v-if="activeUsers.length > 0"
            :users="activeUsers"
            :error="userStore.error"
            @open="$router.push(`/user/${$event}`)"
            @view-all="goToPeople"
            class="md:col-span-2"
          />
          <div
            v-else
            class="md:col-span-2 flex min-h-24 items-center rounded-lg border border-dashed border-outline-variant bg-surface-container-low p-5"
          >
            <p class="font-body text-body-sm text-on-surface-variant">
              {{ t('home.noOneActive') }}
            </p>
          </div>
        </div>
      </section>

      <section class="rounded-xl border border-outline-variant bg-surface-container-lowest p-5 shadow-sm">
        <div class="mb-5">
          <h2 class="font-headline text-headline-md text-primary">{{ t('home.latestPulse') }}</h2>
          <p class="mt-1 font-body text-body-sm text-on-surface-variant">
            {{ t('home.latestPulseDescription') }}
          </p>
        </div>
        <MicroFeed />
      </section>
    </div>
  </LayoutMain>
</template>

<script setup>
import { computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useUserStore } from '../stores/user'
import { usePersonalsStore } from '../stores/personals'
import { MessageCircle, Sparkles, Users } from 'lucide-vue-next'
import ZinInSpelStatus from '../components/ZinInSpelStatus.vue'
import MicroFeed from '../components/MicroFeed.vue'
import LayoutMain from '../components/ui/LayoutMain.vue'
import ActiveUsersPreview from '../components/views/ActiveUsersPreview.vue'

const { t } = useI18n()
const authStore = useAuthStore()
const userStore = useUserStore()
const personalsStore = usePersonalsStore()

const isAuthenticated = computed(() => {
  return (
    authStore.isAuthenticated ||
    import.meta.env.VITE_DEV_AUTH_BYPASS === 'true' ||
    localStorage.getItem('dev-auth-bypass') === 'true'
  )
})
const activeUsers = computed(() => userStore.activeUsers)
const unreadInboxCount = computed(() => personalsStore.unreadInboxCount)
const nextBestAction = computed(() => {
  if (unreadInboxCount.value > 0) {
    return {
      eyebrow: 'Replies need you',
      title: t('home.nextActionRepliesTitle'),
      description: t('home.nextActionRepliesDescription', { count: unreadInboxCount.value }),
      label: t('home.viewReplies'),
      icon: MessageCircle,
      action: goToMyReplies
    }
  }

  if (activeUsers.value.length > 0) {
    return {
      eyebrow: t('home.peopleNearby'),
      title: t('home.nextActionPeopleTitle'),
      description: t('home.nextActionPeopleDescription', { count: activeUsers.value.length }),
      label: t('home.viewPeople'),
      icon: Users,
      action: goToPeople
    }
  }

  return {
    eyebrow: t('home.startHere'),
    title: t('home.nextActionSparkTitle'),
    description: t('home.nextActionSparkDescription'),
    label: t('home.shareSpark'),
    icon: Sparkles,
    action: goToSpark
  }
})

async function loadActiveUsers() {
  if (isAuthenticated.value && authStore.user && userStore.profile) {
    try {
      await userStore.fetchActiveUsers()
    } catch (error) {
      console.error('Failed to fetch active users:', error)
      // Error is already set in store
    }
  }
}

onMounted(async () => {
  await loadActiveUsers()
  if (isAuthenticated.value) {
    try {
      await personalsStore.fetchInboxReplies()
    } catch (error) {
      console.error('Failed to fetch inbox replies:', error)
    }
  }
})

// Watch for authentication changes and fetch active users when user logs in
watch(
  isAuthenticated,
  async newValue => {
    if (newValue) {
      setTimeout(async () => {
        await loadActiveUsers()
      }, 100)
    }
  },
  { immediate: true }
)

watch(
  () => userStore.profile,
  async newProfile => {
    if (newProfile && isAuthenticated.value && authStore.user) {
      await loadActiveUsers()
    }
  },
  { immediate: true }
)

const router = useRouter()

function goToMyReplies() {
  personalsStore.markAllInboxRepliesRead()
  router.push('/my/replies')
}

function goToPeople() {
  router.push('/exploring')
}

function goToSpark() {
  router.push('/spark')
}
</script>
